/**
 * Detects the likely platform based on the user agent string.
 * Note: This is heuristic and not guaranteed to be perfectly accurate.
 */
const getPlatform = ():
  | 'macos'
  | 'windows'
  | 'android'
  | 'linux'
  | 'ios'
  | 'unknown' => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('mac os x')) return 'macos';
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod'))
    return 'ios'; // iOS might also have 'mac os x'
  if (ua.includes('windows')) return 'windows';
  if (ua.includes('android')) return 'android';
  if (ua.includes('linux')) return 'linux'; // Chrome OS might identify as Linux
  return 'unknown';
};

/**
 * Returns a list of preferred English voice names based on the detected platform.
 * Includes generic 'Neural' or 'Enhanced' keywords as lower priority fallbacks.
 */
export const getPreferredEnglishVoiceNames = (
  allVoices: SpeechSynthesisVoice[],
): string[] => {
  const platform = getPlatform();
  let preferredNames: string[] = [];

  switch (platform) {
    case 'macos':
    case 'ios':
      preferredNames = [
        'Samantha',
        'Alex',
        'Daniel',
        'Fiona',
        'Karen',
        'Moira',
        'Rishi',
        'Tessa',
        'Veena',
      ];
      break;
    case 'windows':
      preferredNames = [
        'Microsoft David Desktop',
        'Microsoft Zira Desktop',
        'Microsoft Mark Desktop',
      ];
      break;
    case 'android':
    case 'linux': // Group Linux/Chrome OS with Android for Google voices
      preferredNames = [
        'Google US English',
        'Google UK English Female',
        'Google UK English Male',
      ];
      break;
    default:
      preferredNames = [];
  }

  // Add voices containing 'Neural' or 'Enhanced' from the actual available list
  const keywords = ['neural', 'enhanced', 'premium'];
  const enhancedVoices = allVoices
    .filter(
      (v) =>
        v.lang.startsWith('en') &&
        keywords.some((keyword) => v.name.toLowerCase().includes(keyword)),
    )
    .map((v) => v.name);

  // Combine platform specific and enhanced, removing duplicates
  const combined = [...new Set([...enhancedVoices, ...preferredNames])];

  return combined;
};

/**
 * Sorts an array of SpeechSynthesisVoice objects, prioritizing preferred names.
 */
export const sortVoices = (
  voices: SpeechSynthesisVoice[],
): SpeechSynthesisVoice[] => {
  const preferredNames = getPreferredEnglishVoiceNames(voices);
  const preferredSet = new Set(preferredNames);

  const preferred: SpeechSynthesisVoice[] = [];
  const others: SpeechSynthesisVoice[] = [];

  voices.forEach((voice) => {
    if (preferredSet.has(voice.name)) {
      preferred.push(voice);
    } else {
      others.push(voice);
    }
  });

  // Sort each group alphabetically by name
  preferred.sort((a, b) => a.name.localeCompare(b.name));
  others.sort((a, b) => a.name.localeCompare(b.name));

  return [...preferred, ...others];
};

/**
 * Extracts text content from an element and splits it into sentences.
 * Basic sentence splitting based on ., !, ?.
 * @param element The HTML element containing the text.
 * @returns An array of sentences.
 */
export const extractSentencesFromElement = (
  element: HTMLElement | null,
): string[] => {
  if (!element) return [];

  const textContent = element.textContent || '';

  // Split into sentences (basic implementation)
  // Regex splits on periods, question marks, and exclamation marks
  // followed by a space or end of string, keeping the punctuation.
  const sentenceArray = textContent
    .split(/(?<=[.!?])(?=\s|$)/g) // Positive lookbehind keeps delimiter
    .map((s) => s.trim()) // Trim whitespace from each sentence
    .filter((sentence) => sentence.length > 0); // Remove empty strings

  return sentenceArray;
};

/**
 * Selects the best available English SpeechSynthesisVoice based on preference and availability.
 * @param availableVoices List of all available voices from `speechSynthesis.getVoices()`.
 * @param preferredVoiceName The name of the user's preferred voice (optional).
 * @returns The selected SpeechSynthesisVoice or null if no suitable English voice is found.
 */
export const selectSpeechSynthesisVoice = (
  availableVoices: SpeechSynthesisVoice[],
  preferredVoiceName?: string | null,
): SpeechSynthesisVoice | null => {
  if (!availableVoices || availableVoices.length === 0) {
    return null;
  }

  const englishVoices = availableVoices.filter((voice) =>
    voice.lang.startsWith('en'),
  );
  if (englishVoices.length === 0) {
    return null; // No English voices available
  }

  let selectedVoice: SpeechSynthesisVoice | null = null;

  // 1. Try user's explicit preference
  if (preferredVoiceName) {
    selectedVoice =
      englishVoices.find((voice) => voice.name === preferredVoiceName) || null;
  }

  // 2. Try the browser's default English voice
  if (!selectedVoice) {
    selectedVoice = englishVoices.find((voice) => voice.default) || null;
  }

  // 3. Try platform-specific preferred voices
  if (!selectedVoice) {
    const platformPreferredNames =
      getPreferredEnglishVoiceNames(availableVoices); // Use the existing utility
    for (const name of platformPreferredNames) {
      const found = englishVoices.find((voice) => voice.name === name);
      if (found) {
        selectedVoice = found;
        break;
      }
    }
  }

  // 4. Fallback to the first available English voice
  if (!selectedVoice) {
    selectedVoice = englishVoices[0];
  }

  return selectedVoice;
};

/**
 * Creates and configures a SpeechSynthesisUtterance.
 * @param text The text to be spoken.
 * @param voice The selected SpeechSynthesisVoice.
 * @param rateSetting The desired playback rate (can be number or string), defaults to 1.
 * @returns A configured SpeechSynthesisUtterance instance.
 */
export const createSpeechUtterance = (
  text: string,
  voice: SpeechSynthesisVoice | null,
  rateSetting?: number | string,
): SpeechSynthesisUtterance => {
  const utterance = new SpeechSynthesisUtterance(text);

  if (voice) {
    utterance.voice = voice;
  }

  // Parse and validate the rate
  let rate = 1; // Default rate
  if (rateSetting !== undefined && rateSetting !== null) {
    const parsedRate =
      typeof rateSetting === 'string' ? parseFloat(rateSetting) : rateSetting;
    // Ensure rate is a valid number between 0.1 and 10
    if (isFinite(parsedRate) && parsedRate > 0) {
      rate = Math.min(Math.max(parsedRate, 0.1), 10);
    }
  }
  utterance.rate = rate;

  // You could add other configurations here if needed (pitch, volume, etc.)
  // utterance.pitch = 1;
  // utterance.volume = 1;

  return utterance;
};
